import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import type { GlassCardSpan, GlassCardVariant } from '../types';

import './_glassCard.scss';

/** Props for the GlassCard shell component. */
export interface GlassCardProps {
  title?: ReactNode;
  icon?: [IconPrefix, IconName];
  span?: GlassCardSpan;
  variant?: GlassCardVariant;
  headerExtra?: ReactNode;
  headerExtraClassName?: string;
  headerSub?: ReactNode;
  bottomActions?: ReactNode;
  className?: string;
  /** When true, adds extra horizontal inset on `.glass-card__content` (e.g. room beside overlay controls). */
  addContentGutter?: boolean;
  lightTheme?: boolean;
  children?: ReactNode;
  /** When provided, wraps the header inner content — use to make the header a link with the same element type as the card's content link. */
  headerWrapper?: (children: ReactNode) => ReactNode;
}

export function GlassCard({
  title,
  icon,
  span = 12,
  variant = 'default',
  headerExtra,
  headerExtraClassName,
  headerSub,
  bottomActions,
  className,
  addContentGutter = false,
  lightTheme = false,
  children,
  headerWrapper,
}: GlassCardProps) {
  const hasBottomActions = bottomActions !== null;

  const headerInner = (
    <>
      <div className="glass-card__header-left">
        {icon && <FontAwesomeIcon icon={icon} className="glass-card__icon" />}
        {/* TODO: Confirm intent. Removed the global__text--title class to isolate it from headers/titles used throughout the rest of the app */}
        {/* {title && <h3 className="glass-card__title global__text--title">{title}</h3>} */}
        {title && <h3 className="glass-card__title">{title}</h3>}
      </div>
      {headerExtra && <div className={clsx('glass-card__header-right', headerExtraClassName)}>{headerExtra}</div>}
    </>
  );

  return (
    <div
      className={clsx(
        'glass-card',
        `glass-card--span-${span}`,
        {
          'glass-card--focus': variant === 'focus',
          'glass-card--accent': variant === 'accent',
          'glass-card--light': lightTheme,
        },
        className,
      )}
    >
      {(title ?? icon ?? headerExtra) && (
        <div className={clsx('glass-card__header', { 'glass-card__header--clickable': headerWrapper })}>
          {headerWrapper ? headerWrapper(headerInner) : headerInner}
        </div>
      )}
      {headerSub && <div className="glass-card__header-sub">{headerSub}</div>}
      <div
        className={clsx('glass-card__content', {
          'glass-card__content--gutter': addContentGutter,
        })}
      >
        {children}
      </div>
      {hasBottomActions && <div className="glass-card__bottom-actions">{bottomActions}</div>}
    </div>
  );
}
